import { Invoice } from '@constants'

/** 發票 request 中依索取方式而異的欄位（wire format） */
export interface InvoiceDetailFields {
  business_ubn_title?: string
  business_ubn?: string
  phone_carrier_number?: string
  charity_organization?: string
}

export interface InvoiceDetailFieldsSource extends InvoiceDetailFields {
  /**
   * 同時收 @constants 的 Invoice 與 @hooks-api 的 ReceiptType（兩份同值 enum）。
   * 注意：型別未從 @hooks-api import 以避免 utils ↔ hooks-api circular dependency。
   */
  receipt_type?: Invoice | `${Invoice}` | null
}

const invoiceDetailFieldsResolverMap: Record<Invoice, (source: InvoiceDetailFieldsSource) => InvoiceDetailFields> = {
  [Invoice.BUSINESS_EINVOICE]: source => ({
    business_ubn_title: source.business_ubn_title || source.business_ubn || '', // 抬頭沒寫的話，就填入統一編號
    business_ubn: source.business_ubn ?? '',
  }),
  [Invoice.PHONE_CARRIER]: source => ({ phone_carrier_number: source.phone_carrier_number ?? '' }),
  [Invoice.DONATE_INVOICE]: source => ({ charity_organization: source.charity_organization ?? '' }),
  [Invoice.NON_BUSINESS_EINVOICE]: () => ({}),
}

/**
 * 建單／更新發票時只夾帶當前索取方式該送的欄位。
 * 使用者切換發票類型後，前一類型的殘值（例如統編欄位填成手機條碼字串）若一併送出，ezPay 會以「買受人統編格式不符(限數字)」拒絕開立。
 * 前端驗證是依索取方式條件觸發，擋不下非當前類型的非法殘值，故在 payload 這層過濾。
 */
export const resolveInvoiceDetailFields = (source: InvoiceDetailFieldsSource): InvoiceDetailFields => {
  if (!source.receipt_type) return {}
  const resolveFields = invoiceDetailFieldsResolverMap[source.receipt_type]
  return resolveFields ? resolveFields(source) : {}
}

/**
 * 更新既有交易的發票欄位時，非當前索取方式的欄位要顯式送空字串清掉，
 * 省略 key 會讓後端保留原交易的舊值（例如原本選營業用電子發票留下的統編）。
 */
export const resolveInvoiceUpdateDetailFields = (source: InvoiceDetailFieldsSource): Required<InvoiceDetailFields> => {
  return {
    business_ubn_title: '',
    business_ubn: '',
    phone_carrier_number: '',
    charity_organization: '',
    ...resolveInvoiceDetailFields(source),
  }
}
